/*
 * Shared value formatting for CMMS.
 *
 * Replaces nine local helpers across eight files (5x `formatTimestamp`, 2x `formatDate`,
 * `formatDuration`, `formatNumber`). Every date copy called `d.toLocaleString()` with no
 * locale. That reads the *browser* locale, so switching the app to English left dates in
 * Swedish on a Swedish machine — the app's own language setting was ignored entirely.
 *
 * Built on `intl` rather than utils/dateTimeUtils.js. Both are reasonable; the repo has no
 * single convention (dateTimeUtils in machineDetails, hand-rolled helpers in several
 * features, intl.formatDate in versionNumber). intl wins here because every CMMS component
 * already has an `intl` instance in hand, whereas dateTimeUtils needs `language` threaded
 * down from redux, and because it follows IntlProvider automatically.
 *
 * The options deliberately reproduce dateTimeUtils' output so CMMS does not become a
 * fourth date format:
 *     sv  2026-08-03 / 2026-08-03 14:30
 *     en  08/03/2026 / 08/03/2026, 02:30 PM
 */

// Em dash, not an empty cell — a blank reads as a rendering fault, and "Invalid Date" is
// what the old helpers produced for unparseable input.
export const EMPTY_VALUE = "—";

const DATE_OPTIONS = { year: "numeric", month: "2-digit", day: "2-digit" };
const TIME_OPTIONS = { hour: "2-digit", minute: "2-digit" };

const HAS_TIME = /T\d{2}:\d{2}/;
const HAS_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/*
 * Every CMMS timestamp is UTC, and this is where that is said.
 *
 * The columns are datetime2, which carries no offset, so EF reads them back as
 * DateTimeKind.Unspecified and System.Text.Json then serialises them without a trailing `Z`.
 * `new Date("2026-09-03T08:00:00")` parses a string with no offset as *browser-local* per
 * ES2015+, so a schedule entered at 10:00 in Sweden rendered as 08:00 — on every CMMS surface,
 * work orders included.
 *
 * Putting the missing `Z` back is the whole fix. It is safe because the assumption is total:
 * the services write DateTime.UtcNow, the columns default to getutcdate(), and every write path
 * from the browser sends an ISO `Z` string.
 *
 * IF CMMS EVER ADOPTS THE /activities TRANSPORT CONVENTION — server converts to
 * Company.Timezone and sends a naive local string — THIS FUNCTION IS THE ONE THING TO CHANGE,
 * and it must become the identity. Appending `Z` to a local wall-clock string shifts it the
 * other way, turning a display bug into a silent data-reading bug.
 */
export const asUtcIso = (value) => {
  // Date, dayjs and epoch numbers carry their own instant; only a bare string is ambiguous.
  if (typeof value !== "string") return value;
  // A date with no time ("2026-09-03") is already parsed as UTC midnight by the spec, and
  // "2026-09-03Z" is not a valid date string at all.
  if (!HAS_TIME.test(value) || HAS_OFFSET.test(value)) return value;
  return `${value}Z`;
};

/*
 * The instant a CMMS timestamp names, or null when it names none.
 *
 * Exported for the grids' date columns, which must hand MUI a Date to sort and filter on: the
 * formatted text sorts as text, which is right in Swedish (YYYY-MM-DD) and wrong in English.
 */
export const toValidDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const date = new Date(asUtcIso(value));
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Date only. */
export const formatDay = (intl, value) => {
  const date = toValidDate(value);
  return date ? intl.formatDate(date, DATE_OPTIONS) : EMPTY_VALUE;
};

/** Date and time — the default for audit columns (created, uploaded, checked in). */
export const formatTimestamp = (intl, value) => {
  const date = toValidDate(value);
  if (!date) return EMPTY_VALUE;
  return `${intl.formatDate(date, DATE_OPTIONS)} ${intl.formatTime(date, TIME_OPTIONS)}`;
};

/**
 * Elapsed hours as "2h 30m".
 *
 * Not decimal hours: "2.5 h" is fine for a total but poor for a single labour record,
 * where people think in hours and minutes. Mirrors utils/durationUtils.js, which does the
 * same for minutes elsewhere in the app.
 */
export const formatDurationHours = (intl, hours) => {
  const parsed = Number(hours);
  if (hours === null || hours === undefined || Number.isNaN(parsed)) return EMPTY_VALUE;

  const sign = parsed < 0 ? "-" : "";
  const totalMinutes = Math.round(Math.abs(parsed) * 60);
  const wholeHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${sign}${wholeHours}h ${String(minutes).padStart(2, "0")}m`;
};

/**
 * Byte counts as "2.5 MB". Binary units (1024), matching what file managers report, so the
 * size shown next to a chosen file agrees with the size the OS shows for it.
 */
export const formatFileSize = (bytes) => {
  if (bytes === null || bytes === undefined || Number.isNaN(Number(bytes))) return EMPTY_VALUE;
  if (bytes === 0) return "0 B";

  const units = ["B", "kB", "MB", "GB"];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);

  return `${Math.round((bytes / Math.pow(1024, exponent)) * 100) / 100} ${units[exponent]}`;
};

/** Quantities and costs — locale-aware separators, placeholder instead of NaN. */
export const formatQuantity = (intl, value, fractionDigits = 2) => {
  const parsed = Number(value);
  if (value === null || value === undefined || value === "" || Number.isNaN(parsed)) {
    return EMPTY_VALUE;
  }
  return intl.formatNumber(parsed, { maximumFractionDigits: fractionDigits });
};
